import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import {
	fetchEmployes,
	fetchDepVehicules,
} from "../../store/actions/depannageActions"
import {
	fetchDepSearch,
	setDepSearchCriteres,
} from "../../store/actions/depSearchActions"
import { fetchAutreEntites } from "../../store/actions/autreActions"
import { useGroups } from "../../hooks/groups"
import { generalStyles as gs } from "../styles/styles"
import InputV2 from "../basics/input/InputV2"
import { COLORS } from "../../utilities/Constants"
import {
	dateFrToISO,
	dateISOToFr,
	DEPANNAGE_TYPE_APPEL_CHOICES,
	DEPANNAGE_TYPE_APPEL_POLICE,
	DEPANNAGE_TYPE_APPEL_ASSISTANCE,
	DEPANNAGE_ETAT_CHOICES,
	PROPRE_VEHICULE_TYPE_DEPANNAGE,
} from "../../utilities/Addons"
import { SECTEURS_POLICE } from "../../utilities/Utils"

// Ligne label + champ. ⚠️ Module-level (PAS dans le render) : un composant défini
// dans le render est recréé à chaque rendu → ses enfants sont démontés/remontés à
// chaque frappe → perte de focus (ex. +/- sur les inputs date).
const Field = ({ label, children }) => (
	<div style={gs.fieldRow}>
		<label style={gs.label}>{label}</label>
		{children}
	</div>
)

// Formulaire de recherche dépannage (V2). Page 1/2 : la saisie des critères. Au
// clic « Rechercher », on mémorise les critères, on lance la recherche puis on
// navigue vers la page de résultats (`/depannage/resultats`). Jumeau de
// AtelierSearchV2 (même patron form → résultats).
const DepSearchV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { isBoss } = useGroups()
	const employes = useSelector((state) => state.masterReducer.employes)
	const fetchEmployesStatus = useSelector(
		(state) => state.masterReducer.fetchEmployes,
	)
	const depVehicules = useSelector((state) => state.depInfo.depVehicules)
	const fetchDepVehiculesStatus = useSelector(
		(state) => state.depInfo.fetchDepVehicules,
	)
	const { criteres, loading } = useSelector((state) => state.depSearchInfo)
	// Référentiels des filtres liés au type d'appel (mêmes sources que la fiche).
	const assistances = useSelector((state) => state.autreInfo.assistances)
	// Lieu d'intervention police (GE-7) : enum en dur — SECTEURS_POLICE est
	// la source unique (miroir de LIEU_POLICE_* backend).
	const secteursPolice = Object.keys(SECTEURS_POLICE).map((k) => ({
		id: parseInt(k),
		label: SECTEURS_POLICE[k],
	}))

	// Pré-remplissage depuis la dernière recherche (criteres stockés au format fil :
	// dates DD-MM-YYYY → converties ISO pour les <input type="date"> ; cases "on"/"").
	const [form, setForm] = useState(() => ({
		nom: criteres?.nom || "",
		plaques: criteres?.plaques || "",
		marque: criteres?.marque || "",
		depanneur: criteres?.depanneur || 0,
		appel_type: criteres?.appel_type ?? -1,
		num_dossier: criteres?.num_dossier || "",
		matricule: criteres?.matricule || "",
		vehicule_depannage: criteres?.vehicule_depannage || 0,
		secteur_police: criteres?.secteur_police || 0,
		assistance_entite: criteres?.assistance_entite || 0,
		etat: criteres?.etat ?? -1,
		dateT: criteres?.dateT ?? 0,
		dateS: dateFrToISO(criteres?.dateS || ""),
		dateE: dateFrToISO(criteres?.dateE || ""),
		surPlace: criteres?.surPlace === "on",
		marked: criteres?.marked === "on",
		boss: criteres?.boss === "on",
	}))

	useEffect(() => {
		dispatch(fetchEmployes())
		dispatch(fetchDepVehicules())
		dispatch(fetchAutreEntites()) // filtre assistance (appel = Assistance)
	}, [dispatch])

	const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))
	// Entrée sur « Du » → focus « Au » ; Entrée sur « Au » → lance la recherche.
	const refDateE = useRef(null)

	const reset = () =>
		setForm({
			nom: "",
			plaques: "",
			marque: "",
			depanneur: 0,
			appel_type: -1,
			num_dossier: "",
			matricule: "",
			vehicule_depannage: 0,
			secteur_police: 0,
			assistance_entite: 0,
			etat: -1,
			dateT: 0,
			dateS: "",
			dateE: "",
			surPlace: false,
			marked: false,
			boss: false,
		})

	// Changer le type d'appel réinitialise les filtres conditionnels (secteur /
	// assistance) : ils n'ont de sens que pour Police / Assistance respectivement.
	const setAppelType = (val) =>
		setForm((f) => ({
			...f,
			appel_type: val,
			secteur_police: 0,
			assistance_entite: 0,
			num_dossier: "",
			matricule: "",
		}))

	const search = () => {
		// Format fil attendu par le backend (DepannageView.get) : dates DD-MM-YYYY,
		// ids en nombre (querystring → "0"…), cases à cocher "on"/"". Toutes les clés
		// doivent être présentes (le backend les lit sans .get()).
		const criteres = {
			nom: form.nom,
			plaques: form.plaques,
			marque: form.marque,
			depanneur: form.depanneur,
			appel_type: form.appel_type,
			num_dossier: form.num_dossier,
			matricule: form.matricule,
			vehicule_depannage: form.vehicule_depannage,
			secteur_police: form.secteur_police,
			assistance_entite: form.assistance_entite,
			etat: form.etat,
			dateT: form.dateT,
			dateS: dateISOToFr(form.dateS),
			dateE: dateISOToFr(form.dateE),
			surPlace: form.surPlace ? "on" : "",
			marked: form.marked ? "on" : "",
			boss: form.boss ? "on" : "",
		}
		dispatch(setDepSearchCriteres(criteres))
		dispatch(fetchDepSearch(criteres)).then((json) => {
			// json présent = succès ; undefined sur erreur (catchApiError).
			if (json) history.push("/depannage/resultats")
		})
	}

	const onEnter = (e) => {
		if (e.key === "Enter") search()
	}

	// Raccourcis globaux repris du legacy : F9 réinitialise, Échap revient à l'accueil.
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "F9") {
				e.preventDefault()
				reset()
			} else if (e.key === "Escape") {
				history.push("/")
			}
		}
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [history])

	return (
		<div style={gs.page}>
			<div style={{ maxWidth: 640, margin: "0 auto" }}>
				<div style={gs.header}>
					<div>
						<div style={gs.ficheNumber}>Dépannage</div>
						<div style={gs.title}>Rechercher une fiche</div>
					</div>
				</div>

				<div style={gs.card}>
					<div style={gs.fieldGroup}>
						<Field label="Nom">
							<input
								type="text"
								style={gs.input}
								value={form.nom}
								onChange={(e) => set("nom", e.target.value)}
								onKeyDown={onEnter}
							/>
						</Field>
						<Field label="Plaques">
							<input
								type="text"
								style={{ ...gs.input, textTransform: "uppercase" }}
								value={form.plaques}
								onChange={(e) => set("plaques", e.target.value)}
								onKeyDown={onEnter}
							/>
						</Field>
						<Field label="Marque">
							<input
								type="text"
								style={gs.input}
								value={form.marque}
								onChange={(e) => set("marque", e.target.value)}
								onKeyDown={onEnter}
							/>
						</Field>
						<Field label="Dépanneur">
							{fetchEmployesStatus && fetchEmployesStatus.loading ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								<select
									style={gs.select}
									value={form.depanneur}
									onChange={(e) =>
										set("depanneur", parseInt(e.target.value))
									}
									onKeyDown={onEnter}
								>
									<option value={0}>-</option>
									{employes.map((dep) => (
										<option key={dep.id + dep.nom} value={dep.id}>
											{dep.nom}
										</option>
									))}
								</select>
							)}
						</Field>
						<Field label="Véhicule dépannage">
							{fetchDepVehiculesStatus &&
							fetchDepVehiculesStatus.loading ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								<select
									style={gs.select}
									value={form.vehicule_depannage}
									onChange={(e) =>
										set(
											"vehicule_depannage",
											parseInt(e.target.value),
										)
									}
									onKeyDown={onEnter}
								>
									<option value={0}>-</option>
									{depVehicules
										.filter(
											(v) =>
												v.type ===
												PROPRE_VEHICULE_TYPE_DEPANNAGE,
										)
										.map((v) => (
											<option
												key={v.id + v.nom}
												value={v.id}
											>
												{v.nom}
											</option>
										))}
								</select>
							)}
						</Field>
						<Field label="Type d'appel">
							<div style={{ ...gs.segmented, marginBottom: 0 }}>
								{[[-1, "Tous"], ...DEPANNAGE_TYPE_APPEL_CHOICES].map(
									([val, lbl]) => (
										<button
											key={`appel_${val}`}
											type="button"
											style={gs.segment(
												form.appel_type === val,
											)}
											onClick={() => setAppelType(val)}
										>
											{lbl}
										</button>
									),
								)}
							</div>
						</Field>
						{/* Bloc Police : secteur + matricule (comme la fiche) */}
						{form.appel_type === DEPANNAGE_TYPE_APPEL_POLICE && (
							<>
								<Field label="Lieu d'intervention police">
									<select
										style={gs.select}
										value={form.secteur_police}
										onChange={(e) =>
											set(
												"secteur_police",
												parseInt(e.target.value),
											)
										}
										onKeyDown={onEnter}
									>
										<option value={0}>Tous</option>
										{(secteursPolice || []).map((sec) => (
											<option
												key={`sp_${sec.id}`}
												value={sec.id}
											>
												{sec.label}
											</option>
										))}
									</select>
								</Field>
								<Field label="Matricule">
									<input
										type="text"
										style={{
											...gs.input,
											textTransform: "uppercase",
										}}
										value={form.matricule}
										onChange={(e) =>
											set("matricule", e.target.value)
										}
										onKeyDown={onEnter}
									/>
								</Field>
							</>
						)}
						{/* Bloc Assistance : entité + n° dossier (comme la fiche) */}
						{form.appel_type === DEPANNAGE_TYPE_APPEL_ASSISTANCE && (
							<>
								<Field label="Assistance">
									<select
										style={gs.select}
										value={form.assistance_entite}
										onChange={(e) =>
											set(
												"assistance_entite",
												parseInt(e.target.value),
											)
										}
										onKeyDown={onEnter}
									>
										<option value={0}>Toutes</option>
										{(assistances || []).map((a) => (
											<option
												key={`assist_${a.id}`}
												value={a.id}
											>
												{a.label}
											</option>
										))}
									</select>
								</Field>
								<Field label="N° dossier">
									<input
										type="text"
										style={{
											...gs.input,
											textTransform: "uppercase",
										}}
										value={form.num_dossier}
										onChange={(e) =>
											set("num_dossier", e.target.value)
										}
										onKeyDown={onEnter}
									/>
								</Field>
							</>
						)}
						<Field label="État de la fiche">
							<select
								style={gs.select}
								value={form.etat}
								onChange={(e) =>
									set("etat", parseInt(e.target.value))
								}
								onKeyDown={onEnter}
							>
								<option value={-1}>Tous</option>
								{DEPANNAGE_ETAT_CHOICES.map(([val, lbl]) => (
									<option key={`etat_${val}`} value={val}>
										{lbl}
									</option>
								))}
							</select>
						</Field>
						<Field label="Type de date">
							<select
								style={gs.select}
								value={form.dateT}
								onChange={(e) =>
									set("dateT", parseInt(e.target.value))
								}
							>
								<option value={0}>Dépannage</option>
								<option value={1}>Restitution</option>
								<option value={2}>Payé date inter</option>
								<option value={3}>Payé date facture</option>
								<option value={4}>Fourrière date inter</option>
								<option value={5}>Fourrière date facture</option>
								<option value={6}>Expertise</option>
							</select>
						</Field>
						<Field label="Du">
							<InputV2
								type="date"
								style={gs.input}
								value={form.dateS}
								onChange={(e) => set("dateS", e.target.value)}
								nextRef={refDateE}
							/>
						</Field>
						<Field label="Au">
							<InputV2
								ref={refDateE}
								type="date"
								style={gs.input}
								value={form.dateE}
								onChange={(e) => set("dateE", e.target.value)}
								onEnter={search}
							/>
						</Field>
						<Field label="Marquée">
							<input
								type="checkbox"
								style={{ width: 18, height: 18, cursor: "pointer" }}
								checked={form.marked}
								onChange={() => set("marked", !form.marked)}
							/>
						</Field>
						{isBoss && (
							<Field label="Sur place">
								<input
									type="checkbox"
									style={{
										width: 18,
										height: 18,
										cursor: "pointer",
									}}
									checked={form.surPlace}
									onChange={() => set("surPlace", !form.surPlace)}
								/>
							</Field>
						)}
						{isBoss && (
							<Field label="Boss">
								<input
									type="checkbox"
									style={{
										width: 18,
										height: 18,
										cursor: "pointer",
									}}
									checked={form.boss}
									onChange={() => set("boss", !form.boss)}
								/>
							</Field>
						)}
					</div>
				</div>

				<div style={{ ...gs.footer, marginTop: 20 }}>
					<button
						type="button"
						style={gs.btnSecondary}
						onClick={reset}
					>
						Réinitialiser
					</button>
					<button
						type="button"
						style={gs.btnPrimary}
						onClick={search}
						disabled={loading}
					>
						{loading ? (
							<FontAwesomeIcon icon={faSync} spin />
						) : (
							"Rechercher"
						)}
					</button>
				</div>

				<div
					style={{
						marginTop: 16,
						fontSize: 12,
						color: COLORS.textLight,
						textAlign: "center",
					}}
				>
					Entrée pour lancer la recherche · F9 réinitialiser · Échap accueil
				</div>
			</div>
		</div>
	)
}

export default DepSearchV2
